import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import ImageEditor from './ImageEditor';
import VideoEditor from './VideoEditor';
import ImageNotification from './ImageNotification';
import DecartResultPreview from './DecartResultPreview';
import { decartEdit } from '../Services/ApiServices/chatServices';
import '../Components/Styles/CameraCapture.css';

const CAMERA_STARTUP_TIMEOUT = 10000;
const VIDEO_READY_TIMEOUT = 5000;

const ImageUploadCaptureInline = ({ images = [], onImagesChange, onModalStateChange }) => {
  const fileInputRef = useRef(null);
  const videoFileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const plusButtonRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingDurationRef = useRef(0);
  const recordingStartTimeRef = useRef(null);
  const recordingTimerRef = useRef(null);

  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraFacingMode, setCameraFacingMode] = useState('user');
  const [capturedImage, setCapturedImage] = useState(null);
  const [showPlusModal, setShowPlusModal] = useState(false);
  const [imageToEdit, setImageToEdit] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [videoToEdit, setVideoToEdit] = useState(null);
  const [showVideoEditor, setShowVideoEditor] = useState(false);
  const [editingVideoId, setEditingVideoId] = useState(null);
  const [editingImageId, setEditingImageId] = useState(null);
  const [notification, setNotification] = useState(null);
  const [cameraError, setCameraError] = useState(null);

  const [captureMode, setCaptureMode] = useState('photo');
  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideoUrl, setRecordedVideoUrl] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [decartPrompt, setDecartPrompt] = useState('');
  const [isDecartProcessing, setIsDecartProcessing] = useState(false);
  const [decartResult, setDecartResult] = useState(null);
  const [showDecartPreview, setShowDecartPreview] = useState(false);
  const [decartError, setDecartError] = useState(null);
  const [decartMode, setDecartMode] = useState('lucy');

  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  const MAX_VIDEO_SIZE = 30 * 1024 * 1024;
  const MAX_RECORDING_TIME = 60;

  useEffect(() => {
    const isAnyModalOpen = showCameraModal || showPlusModal || showEditor || showVideoEditor || showDecartPreview;
    if (onModalStateChange) {
      onModalStateChange(isAnyModalOpen);
    }
  }, [showCameraModal, showPlusModal, showEditor, showVideoEditor, showDecartPreview, onModalStateChange]);

  const isSecureContext = () => {
    if (typeof window === 'undefined') return false;
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isLocalhost || window.location.protocol === 'https:';
  };

  const getSecurityErrorMessage = () => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) return null;
    return 'Camera access requires HTTPS. The application is being served over an insecure connection.';
  };

  const buildCameraConstraints = (mode, facingMode) => ({
    video: {
      facingMode,
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
    audio: mode === 'video',
  });

  const requestMediaWithTimeout = async (constraints, timeoutMs) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        ...constraints,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return stream;
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        throw new Error(`Camera startup timed out after ${timeoutMs / 1000}s. If you are in a remote desktop or virtual session, camera passthrough may not be available.`);
      }
      throw err;
    }
  };

  const waitForVideoReady = async (videoEl, timeoutMs) => {
    if (!videoEl) {
      throw new Error('Video element is not available');
    }
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        removeVideoReadyListeners();
        reject(new Error('Video stream did not become ready.'));
      }, timeoutMs);

      const onLoadedMeta = async () => {
        removeVideoReadyListeners();
        clearTimeout(timeoutId);
        try {
          await videoEl.play();
          resolve();
        } catch (playErr) {
          reject(new Error('Video metadata loaded but playback failed: ' + playErr.message));
        }
      };

      const onCanPlay = () => {
        removeVideoReadyListeners();
        clearTimeout(timeoutId);
        resolve();
      };

      const onError = () => {
        removeVideoReadyListeners();
        clearTimeout(timeoutId);
        reject(new Error('Video element encountered an error while loading the stream.'));
      };

      const removeVideoReadyListeners = () => {
        videoEl.removeEventListener('loadedmetadata', onLoadedMeta);
        videoEl.removeEventListener('canplay', onCanPlay);
        videoEl.removeEventListener('error', onError);
      };

      videoEl.addEventListener('loadedmetadata', onLoadedMeta);
      videoEl.addEventListener('canplay', onCanPlay);
      videoEl.addEventListener('error', onError);
    });
  };

  const logStreamTracks = (stream) => {
    console.log('Stream tracks:', stream.getTracks().map((track) => ({
      kind: track.kind,
      label: track.label,
      enabled: track.enabled,
      readyState: track.readyState,
    })));
  };

  const resolveCameraErrorMessage = (error) => {
    if (error?.name === 'NotReadableError') {
      return 'Could not start video source. Camera may be in use by another application or unavailable. Close other apps using the camera and try again.';
    }
    if (error?.name === 'NotAllowedError') {
      return 'Camera/microphone access denied. Please allow permissions.';
    }
    if (error?.name === 'NotFoundError') {
      return 'No camera or microphone found. If you are using a remote desktop or virtual environment, camera passthrough may not be available.';
    }
    if (error?.name === 'TypeError' && error?.message?.includes('insecure')) {
      return 'Camera access blocked: Application must be served over HTTPS.';
    }
    if (error?.message?.includes('timed out')) {
      return 'Camera startup timed out. If you are in a remote desktop or virtual session, camera passthrough may not be configured.';
    }
    return error?.message || 'Could not access camera.';
  };

  const stopCamera = useCallback(() => {
    console.log('stopCamera called');
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch (error) {
        console.error('Error stopping MediaRecorder on camera close:', error);
      }
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    recordingDurationRef.current = 0;
    recordingStartTimeRef.current = null;
    recordedChunksRef.current = [];
    setIsCameraOpen(false);
    setShowCameraModal(false);
    setCapturedImage(null);
    setIsRecording(false);
    setRecordedVideoUrl(null);
    setRecordingDuration(0);
    setCaptureMode('photo');
    setCameraError(null);
    console.log('Camera closed');
  }, []);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  useEffect(() => {
    const handleDocClick = (e) => {
      if (!showPlusModal) return;
      if (plusButtonRef.current && !plusButtonRef.current.contains(e.target)) {
        setShowPlusModal(false);
      }
    };
    document.addEventListener('click', handleDocClick);
    return () => document.removeEventListener('click', handleDocClick);
  }, [showPlusModal]);

  const startCamera = async (mode = 'photo') => {
    if (!isSecureContext()) {
      const securityError = getSecurityErrorMessage();
      if (securityError) {
        console.error('Security error: Camera cannot be accessed over insecure (HTTP) connection');
      }
      return;
    }

    setCameraError(null);
    setCaptureMode(mode);
    setShowCameraModal(true);
    try {
      const constraints = buildCameraConstraints(mode, cameraFacingMode);
      console.log('Requesting camera with constraints:', constraints);

      let stream;
      try {
        stream = await requestMediaWithTimeout(constraints, CAMERA_STARTUP_TIMEOUT);
      } catch (audioError) {
        console.warn("Camera with audio not available, falling back to video-only:", audioError);
        stream = await requestMediaWithTimeout(buildCameraConstraints(mode, cameraFacingMode), CAMERA_STARTUP_TIMEOUT);
      }

      streamRef.current = stream;
      logStreamTracks(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await waitForVideoReady(videoRef.current, VIDEO_READY_TIMEOUT);
      }

      setIsCameraOpen(true);
      console.log(`Camera opened with facing mode: ${cameraFacingMode}, mode: ${mode}`);
    } catch (error) {
      const errorMessage = resolveCameraErrorMessage(error);
      console.error('Error accessing camera:', error);
      alert(`Camera error: ${errorMessage}`);
      setCameraError(errorMessage);
      setShowCameraModal(false);
      stopCamera();
    }
  };

  const switchCamera = async () => {
    const newFacingMode = cameraFacingMode === 'user' ? 'environment' : 'user';
    setCameraFacingMode(newFacingMode);

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }

    try {
      const constraints = buildCameraConstraints(captureMode, newFacingMode);
      let stream;
      try {
        stream = await requestMediaWithTimeout(constraints, CAMERA_STARTUP_TIMEOUT);
      } catch (audioError) {
        console.warn("Audio not available during camera switch, falling back:", audioError);
        stream = await requestMediaWithTimeout(buildCameraConstraints(captureMode, newFacingMode), CAMERA_STARTUP_TIMEOUT);
      }

      streamRef.current = stream;
      logStreamTracks(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await waitForVideoReady(videoRef.current, VIDEO_READY_TIMEOUT);
      }

      console.log(`Switched to ${newFacingMode === 'user' ? 'front' : 'back'} camera`);
    } catch (error) {
      console.error('Error switching camera:', error);
      const errorMessage = resolveCameraErrorMessage(error);
      alert(`Camera switch error: ${errorMessage}`);
      setCameraError(errorMessage);
      setShowCameraModal(false);
      stopCamera();
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const getBase64Size = (base64String) => {
    const stringLength = base64String.length - (base64String.indexOf(',') + 1);
    const sizeInBytes = 4 * Math.ceil(stringLength / 3) * 0.5624896334383812;
    return sizeInBytes;
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const handleFileSelected = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      showNotification(
        `Image size (${sizeMB}MB) exceeds the 5MB limit. Please choose a smaller image.`,
        'error'
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result;
      const base64Size = getBase64Size(base64);
      if (base64Size > MAX_FILE_SIZE) {
        const sizeMB = (base64Size / (1024 * 1024)).toFixed(2);
        showNotification(
          `Converted image size (${sizeMB}MB) exceeds the 5MB limit.`,
          'error'
        );
        return;
      }

      setImageToEdit(base64);
      setShowEditor(true);
      setShowPlusModal(false);
      showNotification('Image loaded successfully! You can now edit it.', 'success');
    };
    reader.readAsDataURL(file);
  };

  const startRecording = () => {
    if (!streamRef.current) {
      showNotification('No camera stream available', 'error');
      return;
    }

    if (!window.MediaRecorder) {
      showNotification('Video recording is not supported in your browser', 'error');
      return;
    }

    try {
      recordedChunksRef.current = [];
      recordingDurationRef.current = 0;
      recordingStartTimeRef.current = Date.now();

      let options = { mimeType: 'video/webm;codecs=vp8,opus' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        console.warn('vp8,opus not supported, trying vp8 only');
        options = { mimeType: 'video/webm;codecs=vp8' };
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          console.warn('vp8 not supported, trying default webm');
          options = { mimeType: 'video/webm' };
          if (!MediaRecorder.isTypeSupported(options.mimeType)) {
            console.warn('webm not supported, using browser default');
            options = {};
          }
        }
      }

      console.log('Creating MediaRecorder with options:', options);
      const mediaRecorder = new MediaRecorder(streamRef.current, options);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          console.log('Data chunk received:', event.data.size, 'bytes');
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const actualDuration = recordingStartTimeRef.current
          ? Math.round((Date.now() - recordingStartTimeRef.current) / 1000)
          : recordingDurationRef.current;

        console.log('Recording stopped. Chunks:', recordedChunksRef.current.length, 'Duration:', actualDuration);

        if (recordedChunksRef.current.length === 0) {
          showNotification('No video data recorded. Please try again.', 'error');
          setIsRecording(false);
          return;
        }

        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        console.log('Blob created. Size:', blob.size, 'bytes');

        if (blob.size === 0) {
          showNotification('Recording failed. Please try again.', 'error');
          recordedChunksRef.current = [];
          setIsRecording(false);
          return;
        }

        if (blob.size > MAX_VIDEO_SIZE) {
          const sizeMB = (blob.size / (1024 * 1024)).toFixed(2);
          showNotification(
            `Video size (${sizeMB}MB) exceeds the 30MB limit. Try recording a shorter video.`,
            'error'
          );
          recordedChunksRef.current = [];
          setIsRecording(false);
          return;
        }

        const videoUrl = URL.createObjectURL(blob);
        setRecordedVideoUrl(videoUrl);

        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Video = reader.result;
          setVideoToEdit({
            src: base64Video,
            recordedDuration: actualDuration,
          });
          setShowVideoEditor(true);
          stopCamera();
          showNotification(
            `Video recorded successfully! Duration: ${actualDuration}s. You can now edit it.`,
            'success'
          );
        };
        reader.readAsDataURL(blob);

        setIsRecording(false);
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        showNotification(`Recording error: ${event.error?.message || 'Unknown error'}`, 'error');
        setIsRecording(false);
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setRecordingDuration(0);

      const timer = setInterval(() => {
        recordingDurationRef.current += 1;
        setRecordingDuration((prev) => {
          const newDuration = prev + 1;
          if (newDuration >= MAX_RECORDING_TIME) {
            stopRecording();
          }
          return newDuration;
        });
      }, 1000);
      recordingTimerRef.current = timer;

      console.log('Recording started with mime type:', mediaRecorder.mimeType);
      showNotification('Recording started', 'success');
    } catch (error) {
      console.error('Error starting recording:', error);
      showNotification(`Recording error: ${error.message}`, 'error');
    }
  };

  const stopRecording = () => {
    console.log('stopRecording called');
    console.log('MediaRecorder state:', mediaRecorderRef.current?.state);
    console.log('Chunks so far:', recordedChunksRef.current.length);

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      console.log('Stopping recording...');
      try {
        mediaRecorderRef.current.stop();
        console.log('MediaRecorder.stop() called successfully');
      } catch (error) {
        console.error('Error stopping recording:', error);
        showNotification('Error stopping recording', 'error');
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    } else {
      console.warn('Cannot stop recording - MediaRecorder is not active');
    }
  };

  const handleVideoFileSelected = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (file.size > MAX_VIDEO_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      showNotification(
        `Video size (${sizeMB}MB) exceeds the 30MB limit. Please choose a smaller video.`,
        'error'
      );
      if (videoFileInputRef.current) {
        videoFileInputRef.current.value = '';
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setVideoToEdit(reader.result);
      setShowVideoEditor(true);
      setShowPlusModal(false);
      showNotification('Video loaded successfully! You can now edit it.', 'success');
    };
    reader.readAsDataURL(file);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      console.error('Video or canvas element not found');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    if (cameraFacingMode === 'user') {
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    context.setTransform(1, 0, 0, 1, 0, 0);

    const base64Image = canvas.toDataURL('image/jpeg', 0.8);

    const imageSize = getBase64Size(base64Image);
    if (imageSize > MAX_FILE_SIZE) {
      const sizeMB = (imageSize / (1024 * 1024)).toFixed(2);
      showNotification(
        `Captured image size (${sizeMB}MB) exceeds the 5MB limit. Try reducing camera resolution.`,
        'error'
      );
      return;
    }

    setCapturedImage(base64Image);
    setImageToEdit(base64Image);
    setShowEditor(true);
    stopCamera();
    showNotification('Photo captured successfully! You can now edit it.', 'success');
  };

  const removeImage = (imageId) => {
    const itemToRemove = images.find((img) => img.id === imageId);
    onImagesChange(images.filter((img) => img.id !== imageId));

    if (itemToRemove?.type === 'video' && itemToRemove.src?.startsWith('blob:')) {
      URL.revokeObjectURL(itemToRemove.src);
    }

    showNotification(
      `${itemToRemove?.type === 'video' ? 'Video' : 'Image'} removed successfully`,
      'success'
    );
  };

  const handleEditorSave = (editedImageData) => {
    const imageSize = getBase64Size(editedImageData.src);
    if (imageSize > MAX_FILE_SIZE) {
      const sizeMB = (imageSize / (1024 * 1024)).toFixed(2);
      showNotification(
        `Edited image size (${sizeMB}MB) exceeds the 5MB limit. Try reducing quality or size.`,
        'error'
      );
      return;
    }

    const imageData = {
      src: editedImageData.src,
      type: 'edited',
      format: editedImageData.format,
      size: editedImageData.size,
      edited: true,
      rotation: editedImageData.rotation,
      timestamp: editedImageData.timestamp,
    };

    let updatedImages;
    if (editingImageId) {
      updatedImages = images.map((img) =>
        img.id === editingImageId ? { ...imageData, id: editingImageId } : img
      );
      showNotification(
        `Image updated successfully! Format: ${editedImageData.format.toUpperCase()}, Size: ${editedImageData.size}KB`,
        'success'
      );
    } else {
      const newImage = { ...imageData, id: Date.now() };
      updatedImages = [...images, newImage];
      showNotification(
        `Image saved successfully! Format: ${editedImageData.format.toUpperCase()}, Size: ${editedImageData.size}KB`,
        'success'
      );
    }

    onImagesChange(updatedImages);
    setShowEditor(false);
    setImageToEdit(null);
    setEditingImageId(null);
    setCapturedImage(null);
  };

  const handleEditorCancel = () => {
    setShowEditor(false);
    setImageToEdit(null);
    setEditingImageId(null);
    setCapturedImage(null);
  };

  const handleVideoEditorSave = (editedVideoData) => {
    const videoData = {
      src: editedVideoData.src,
      type: 'video',
      format: editedVideoData.format,
      duration: editedVideoData.duration,
      trimStart: editedVideoData.trimStart,
      trimEnd: editedVideoData.trimEnd,
      volume: editedVideoData.volume,
      filter: editedVideoData.filter,
      edited: editedVideoData.edited,
      timestamp: editedVideoData.timestamp,
      size: (editedVideoData.estimatedSize / 1024).toFixed(2),
    };

    let updatedImages;
    if (editingVideoId) {
      updatedImages = images.map((img) =>
        img.id === editingVideoId ? { ...videoData, id: editingVideoId } : img
      );
      showNotification(
        `Video updated successfully! Duration: ${editedVideoData.duration.toFixed(1)}s`,
        'success'
      );
    } else {
      const newVideo = { ...videoData, id: Date.now() };
      updatedImages = [...images, newVideo];
      showNotification(
        `Video saved successfully! Duration: ${editedVideoData.duration.toFixed(1)}s`,
        'success'
      );
    }

    onImagesChange(updatedImages);
    setShowVideoEditor(false);
    setVideoToEdit(null);
    setEditingVideoId(null);
  };

  const handleVideoEditorCancel = () => {
    setShowVideoEditor(false);
    setVideoToEdit(null);
    setEditingVideoId(null);
  };

  const editVideo = (videoId) => {
    const videoToEditData = images.find((img) => img.id === videoId);
    if (videoToEditData && videoToEditData.type === 'video') {
      setVideoToEdit({
        src: videoToEditData.src,
        recordedDuration: videoToEditData.duration || videoToEditData.recordedDuration || 0,
        trimStart: videoToEditData.trimStart || 0,
        trimEnd: videoToEditData.trimEnd || videoToEditData.duration || 0,
        filter: videoToEditData.filter || 'none',
        volume: videoToEditData.volume || 1,
      });
      setEditingVideoId(videoId);
      setShowVideoEditor(true);
    }
  };

  const editImage = (imageId) => {
    const imageToEditData = images.find((img) => img.id === imageId);
    if (imageToEditData && imageToEditData.type !== 'video') {
      setImageToEdit(imageToEditData.src);
      setEditingImageId(imageId);
      setShowEditor(true);
    }
  };

  const dataURLtoBlob = (dataURL) => {
    if (!dataURL) return null;
    const parts = dataURL.split(';base64,');
    const contentType = parts[0].split(':')[1] || 'image/jpeg';
    const byteCharacters = atob(parts[1]);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays, { type: contentType });
  };

  const processWithDecart = async (mediaData, isVideo = false) => {
    console.log('[UAT_DEBUG] processWithDecart called', { hasMediaData: !!mediaData, isVideo });
    if (!mediaData) return;
    
    setIsDecartProcessing(true);
    setDecartError(null);
    
    try {
      const blob = dataURLtoBlob(mediaData);
      const fileType = isVideo ? 'video/webm' : 'image/jpeg';
      const fileName = isVideo ? 'recorded_video.webm' : 'captured_image.jpg';
      
      const file = new File([blob], fileName, { type: fileType });
      
      const formData = new FormData();
      formData.append('image', file);
      formData.append('prompt', decartPrompt && decartPrompt.trim().length > 0 
        ? decartPrompt 
        : (isVideo 
          ? 'Apply professional video enhancement, improve lighting and color grading'
          : 'Apply professional photo enhancement, improve lighting, color balance, and sharpness'));
      formData.append('mode', decartMode);
      console.log('[UAT_DEBUG] Calling decartEdit API with mode:', decartMode);

      await decartEdit(formData, (res) => {
        console.log('[UAT_DEBUG] decartEdit response:', res?.data ? 'has data' : 'no data');
        if (res?.data?.data) {
          setDecartResult(res.data.data);
          setShowDecartPreview(true);
        } else if (res?.data) {
          setDecartResult(res.data);
          setShowDecartPreview(true);
        } else {
          throw new Error('Invalid response from Decart API');
        }
      }, (err) => {
        const errorMsg = err?.response?.data?.message || err?.message || 'Failed to process media with Decart AI';
        setDecartError(errorMsg);
        showNotification(`Decart AI error: ${errorMsg}`, 'error');
      });
      
    } catch (error) {
      console.error('Decart processing error:', error);
      setDecartError(error.message);
      showNotification(`Decart AI error: ${error.message}`, 'error');
    } finally {
      setIsDecartProcessing(false);
    }
  };

  const handleDecartPreviewSave = (resultData) => {
    const newImage = {
      id: Date.now(),
      src: resultData.src,
      type: 'decart',
      format: resultData.format || 'jpeg',
      size: resultData.size,
      edited: true,
      timestamp: new Date().toISOString()
    };
    
    onImagesChange([...images, newImage]);
    setDecartResult(null);
    setShowDecartPreview(false);
    setDecartPrompt('');
    showNotification('Decart AI result saved successfully!', 'success');
  };

  const handleDecartPreviewCancel = () => {
    setDecartResult(null);
    setShowDecartPreview(false);
    setDecartPrompt('');
    setDecartError(null);
  };

  return (
    <div className="input-with-plus">
      {showCameraModal && (
        <div className="camera-modal">
          <div className="camera-modal-header">
            <h3>
              {captureMode === 'video'
                ? recordedVideoUrl
                  ? 'Review Video'
                  : isRecording
                  ? 'Recording...'
                  : 'Record Video'
                : capturedImage
                ? 'Review Photo'
                : 'Capture Photo'}
            </h3>
            <button
              className="camera-close-btn"
              onClick={stopCamera}
              aria-label="Close camera"
            >
              ✕
            </button>
          </div>

          <div className="camera-content">
            {!isCameraOpen && !capturedImage && !recordedVideoUrl && (
              <div className="camera-loading">
                <p>📷 Initializing camera...</p>
              </div>
            )}

<video
               ref={videoRef}
               className={`camera-video ${capturedImage || recordedVideoUrl ? 'hidden' : ''}`}
               playsInline
               autoPlay
               muted
             />

             {isRecording && (
               <div className="recording-indicator">
                 <span className="recording-dot"></span>
                 <span className="recording-time">
                   {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}
                   {' / '}
                   {Math.floor(MAX_RECORDING_TIME / 60)}:{(MAX_RECORDING_TIME % 60).toString().padStart(2, '0')}
                 </span>
               </div>
             )}

             <canvas ref={canvasRef} style={{ display: 'none' }} />

             {capturedImage && (
               <div className="preview-container">
                 <img src={capturedImage} alt="Captured" className="captured-preview" />
                 <div className="preview-info">
                   <p>📸 Photo Captured</p>
                   <p className="timestamp">{new Date().toLocaleTimeString()}</p>
                 </div>
               </div>
             )}

             {recordedVideoUrl && (
               <div className="preview-container">
                 <video src={recordedVideoUrl} className="captured-preview" controls playsInline />
                 <div className="preview-info">
                   <p>🎥 Video Recorded</p>
                   <p className="timestamp">Duration: {recordingDuration}s</p>
                 </div>
               </div>
             )}

             {(capturedImage || recordedVideoUrl) && !isRecording && (
               <div className="decart-prompt-section">
                 <input
                   type="text"
                   placeholder="Enter prompt for Decart AI (optional)..."
                   value={decartPrompt}
                   onChange={(e) => setDecartPrompt(e.target.value)}
                   className="decart-prompt-input"
                 />
                 <select
                   value={decartMode}
                   onChange={(e) => setDecartMode(e.target.value)}
                   className="decart-mode-select"
                 >
                   <option value="lucy">Lucy (General Edit)</option>
                   <option value="virtual-try-on">Virtual Try-On</option>
                 </select>
                 <button
                   className="camera-btn decart-btn"
                   onClick={() => processWithDecart(capturedImage || recordedVideoUrl, !!recordedVideoUrl)}
                   disabled={isDecartProcessing}
                   title="Process with Decart AI"
                 >
                   {isDecartProcessing ? '⏳ Processing...' : '🤖 Process with Decart AI'}
                 </button>
               </div>
             )}
           </div>

          <div className="camera-controls">
            {captureMode === 'photo' ? (
              !capturedImage ? (
                <>
                  <button
                    className="camera-btn switch-btn"
                    onClick={switchCamera}
                    title="Switch between front and back camera"
                  >
                    🔄 Switch
                  </button>
                  <button
                    className="camera-btn capture-btn"
                    onClick={capturePhoto}
                    title="Capture photo"
                  >
                    📸 Capture
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="camera-btn retake-btn"
                    onClick={async () => {
                      setCapturedImage(null);
                      await startCamera('photo');
                    }}
                    title="Retake photo"
                  >
                    🔄 Retake
                  </button>
                  <button
                    className="camera-btn close-btn"
                    onClick={() => {
                      stopCamera();
                      setCapturedImage(null);
                    }}
                    title="Close camera"
                  >
                    ✓ Done
                  </button>
                </>
              )
            ) : (
              !recordedVideoUrl ? (
                <>
                  {!isRecording && (
                    <button
                      className="camera-btn switch-btn"
                      onClick={switchCamera}
                      title="Switch between front and back camera"
                    >
                      🔄 Switch
                    </button>
                  )}
                  <button
                    className={`camera-btn ${isRecording ? 'stop-btn' : 'record-btn'}`}
                    onClick={isRecording ? stopRecording : startRecording}
                    title={isRecording ? 'Stop recording' : 'Start recording'}
                  >
                    {isRecording ? '⏹ Stop' : '⏺ Record'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="camera-btn retake-btn"
                    onClick={() => {
                      if (recordedVideoUrl) {
                        URL.revokeObjectURL(recordedVideoUrl);
                      }
                      setRecordedVideoUrl(null);
                      setRecordingDuration(0);
                      startCamera('video');
                    }}
                    title="Record again"
                  >
                    🔄 Re-record
                  </button>
                  <button
                    className="camera-btn close-btn"
                    onClick={() => {
                      stopCamera();
                      if (recordedVideoUrl) {
                        URL.revokeObjectURL(recordedVideoUrl);
                      }
                      setRecordedVideoUrl(null);
                    }}
                    title="Close camera"
                  >
                    ✓ Done
                  </button>
                </>
              )
            )}
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
        value=""
      />
      <input
        ref={videoFileInputRef}
        type="file"
        accept="video/*"
        style={{ display: 'none' }}
        onChange={handleVideoFileSelected}
        value=""
      />

      <button
        ref={plusButtonRef}
        name="openPlusInline"
        className="plus-button-inline"
        onClick={(e) => { e.stopPropagation(); setShowPlusModal((s) => !s); }}
        title="Options"
        aria-haspopup="true"
        aria-expanded={showPlusModal}
      >
        ➕
      </button>

      {images.length > 0 && (
        <div className="inline-image-preview">
          {images.map((image) => (
            <div key={image.id} className="inline-preview-item">
              {image.type === 'video' ? (
                <>
                  <video src={image.src} className="preview-video" controls playsInline />
                  <button
                    className="inline-preview-edit"
                    onClick={() => editVideo(image.id)}
                    aria-label="Edit video"
                    title="Edit video"
                  >
                    ✏️
                  </button>
                </>
              ) : (
                <>
                  <img src={image.src} alt="Preview" />
                  <button
                    className="inline-preview-edit"
                    onClick={() => editImage(image.id)}
                    aria-label="Edit image"
                    title="Edit image"
                  >
                    ✏️
                  </button>
                </>
              )}
              {(image.edited || image.type === 'video') && (
                <div className="image-metadata">
                  {image.type === 'video' && <span className="type-badge">🎥</span>}
                  <span className="format-badge">{image.format?.toUpperCase()}</span>
                  {image.size && <span className="size-badge">{image.size}KB</span>}
                  {image.duration && <span className="duration-badge">{image.duration}s</span>}
                </div>
              )}
              <button
                className="inline-preview-remove"
                onClick={() => removeImage(image.id)}
                aria-label={`Remove ${image.type === 'video' ? 'video' : 'image'}`}
                title="Remove"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {showPlusModal && (
        <div
          className="plus-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowPlusModal(false);
            }
          }}
        >
          <div className="plus-modal">
            <button
              className="plus-option"
              onClick={() => { openFilePicker(); setShowPlusModal(false); }}
              title="Upload image"
            >
              <span className="plus-icon">📁</span>
              <span>Upload image</span>
            </button>
            <button
              className="plus-option"
              onClick={() => { setShowPlusModal(false); startCamera('photo'); }}
              title="Capture image"
            >
              <span className="plus-icon">📷</span>
              <span>Capture image</span>
            </button>
            <button
              className="plus-option"
              onClick={() => {
                videoFileInputRef.current?.click();
                setShowPlusModal(false);
              }}
              title="Upload video"
            >
              <span className="plus-icon">📂</span>
              <span>Upload video</span>
            </button>
            <button
              className="plus-option"
              onClick={() => { setShowPlusModal(false); startCamera('video'); }}
              title="Record video"
            >
              <span className="plus-icon">🎥</span>
              <span>Record video</span>
            </button>
            <button className="plus-close" onClick={() => setShowPlusModal(false)} aria-label="Close">✕</button>
          </div>
        </div>
      )}

      {showEditor && imageToEdit && (
        <ImageEditor
          image={imageToEdit}
          onSave={handleEditorSave}
          onCancel={handleEditorCancel}
        />
      )}

{showVideoEditor && videoToEdit && (
        <VideoEditor
          video={videoToEdit}
          onSave={handleVideoEditorSave}
          onCancel={handleVideoEditorCancel}
        />
      )}

      {showDecartPreview && decartResult && (
        <DecartResultPreview
          result={decartResult}
          onSave={handleDecartPreviewSave}
          onCancel={handleDecartPreviewCancel}
        />
      )}

      {notification && (
        <ImageNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
          duration={4000}
        />
      )}
    </div>
  );
};

ImageUploadCaptureInline.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      src: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['upload', 'camera', 'edited', 'video', 'decart']).isRequired,
      format: PropTypes.string,
      size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      duration: PropTypes.number,
      timestamp: PropTypes.string,
    })
  ),
  onImagesChange: PropTypes.func.isRequired,
  onModalStateChange: PropTypes.func,
};

export default ImageUploadCaptureInline;
